import { translate } from '@/plugins/i18n'
import MessageHelper from '@/services/helpers/MessageHelper'
import { useAddressesStore } from '@/stores/addresses'
import { useCartStore } from '@/stores/cart'
import { useCustomerStore } from '@/stores/customer'
import { useGuestCheckoutStore } from '@/stores/guestCheckout'
import { useOpcStore } from '@/stores/opc'
import { usePaymentStore } from '@/stores/payment'
import { useRegularCheckoutStore } from '@/stores/regularCheckout'
import { ensureAddressFields, getPaymentData } from '@/utils/checkoutHelpers'

type CheckoutStore =
  | ReturnType<typeof useRegularCheckoutStore>
  | ReturnType<typeof useGuestCheckoutStore>
  | ReturnType<typeof useOpcStore>

type PaymentMethodIdentifier = number | string | null
type CheckoutType = 'guest' | 'express' | 'regular' | 'opc'

const EXPRESS_PAYMENT_METHODS = new Set([
  'paypal_express',
  'apple_pay',
  'google_pay'
])

/**
 * Customer-facing fields every checkout store exposes.
 */
type CommonCheckoutStore = {
  email?: string | null
  useSameBillingAddress?: boolean | null
  subscribeToNewsletter?: boolean | null
  doiOptIn?: boolean | null
  ageData?: any
  phoneNumber?: any
}

/**
 * Extracts the payment method identifier from selectedPaymentMethodData.
 * Used by GuestCheckoutStrategy and OpcCheckoutStrategy.
 */
function getPaymentMethodIdentifierFromData(
  paymentMethodData: unknown
): PaymentMethodIdentifier {
  const data = paymentMethodData as Record<string, unknown> | null
  return (data?.method_code as string | undefined) ?? null
}

/**
 * Accessors shared by every checkout strategy — the underlying stores differ,
 * but they all carry the same customer-facing fields.
 */
abstract class BaseCheckoutStrategy {
  protected abstract readonly store: CommonCheckoutStore

  getEmail(): string | null | undefined {
    return this.store.email
  }

  getUseSameBillingAddress(): boolean | null | undefined {
    return this.store.useSameBillingAddress
  }

  getSubscribeToNewsletter(): boolean | null | undefined {
    return this.store.subscribeToNewsletter
  }

  getDoiOptIn(): boolean | null | undefined {
    return this.store.doiOptIn
  }

  getAgeData(): any {
    return this.store.ageData
  }

  getPhoneNumber(): string | null | undefined {
    return this.store.phoneNumber
  }

  clearAgeAndPhoneData(): void {
    this.store.ageData = null
    this.store.phoneNumber = null
  }
}

/**
 * Shared behaviour for strategies that identify the selected payment method by
 * its data instead of by a payment method id (guest and OPC checkout).
 */
abstract class DataBasedPaymentMethodStrategy extends BaseCheckoutStrategy {
  protected abstract readonly store: CommonCheckoutStore & {
    selectedPaymentMethodData: unknown
    selectedShippingAddressData: unknown
    selectedBillingAddressData: unknown
    blacklistedPaymentMethodCodes: Array<string>
  }

  getPaymentMethodIdentifier(): PaymentMethodIdentifier {
    return getPaymentMethodIdentifierFromData(
      this.store.selectedPaymentMethodData
    )
  }

  /**
   * Returns true if the selected payment method is an express payment method
   * (PayPal Express, Apple Pay, Google Pay).
   * Express payment methods should not include guest flag in the payload.
   */
  isExpressPaymentMethod(): boolean {
    const paymentMethodCode = (
      this.store.selectedPaymentMethodData as Record<string, unknown>
    )?.method_code as string | undefined
    return EXPRESS_PAYMENT_METHODS.has(paymentMethodCode ?? '')
  }

  /**
   * Address and payment selection checks shared by guest and OPC checkout.
   */
  validateCheckoutData(): boolean {
    if (!this.store.selectedShippingAddressData) {
      MessageHelper.addErrorMessage(
        translate('checkout.error.shippingAddressRequired')
      )
      return false
    }
    if (
      !this.store.selectedBillingAddressData &&
      !this.store.useSameBillingAddress &&
      !this.store.selectedShippingAddressData
    ) {
      MessageHelper.addErrorMessage(
        translate('checkout.error.billingAddressRequired')
      )
      return false
    }
    if (!this.store.selectedPaymentMethodData) {
      MessageHelper.addErrorMessage(
        translate('checkout.error.paymentMethodRequired')
      )
      return false
    }
    return true
  }

  blacklistPaymentMethod(idOrCode: number | string): void {
    if (typeof idOrCode !== 'string') {
      return
    }
    if (!this.store.blacklistedPaymentMethodCodes.includes(idOrCode)) {
      this.store.blacklistedPaymentMethodCodes.push(idOrCode)
    }
  }

  clearPaymentMethod(): void {
    this.store.selectedPaymentMethodData = null
  }
}

/**
 * Strategy interface for different checkout implementations.
 *
 * The checkout flow follows these steps:
 * 1. validateForms() - Validate UI forms
 * 2. validateCheckoutData() - Validate strategy-specific data
 * 3. beforeOrder() - Pre-order actions (e.g., add product to cart, set defaults)
 * 4. Build payload with addAddressFieldsToPayload() and addPaymentFieldsToPayload()
 * 5. modifyPayload() - Final payload modifications
 * 6. 3DS check
 * 7. Place order (placeOrder or placeExpressOrder)
 * 8. afterPurchaseCleanup() - Post-order cleanup
 */
export interface CheckoutStrategy {
  /**
   * Whether this is a guest checkout (uses placeExpressOrder API)
   */
  readonly isGuest: boolean

  /**
   * Whether this is an OPC checkout
   */
  readonly isOpc: boolean

  /**
   * Whether to use the express order API endpoint.
   * For most strategies this equals isGuest, but OPC can use express order
   * for certain payment methods (PayPal Express, Apple Pay, Google Pay).
   */
  shouldUseExpressOrder(): boolean

  /**
   * Returns true if the selected payment method is an express payment method
   * (PayPal Express, Apple Pay, Google Pay).
   * Express payment methods should not include guest flag in the payload.
   */
  isExpressPaymentMethod(): boolean

  /**
   * Identifies the checkout type for the API payload.
   */
  getCheckoutType(): CheckoutType

  /**
   * Optional pre-order actions (e.g., add product to cart, set addresses as default).
   * Called before building the payload.
   */
  beforeOrder?(): Promise<void>

  /**
   * Optional final payload modifications after all fields have been added.
   * Called after addAddressFieldsToPayload and addPaymentFieldsToPayload.
   */
  modifyPayload?(payload: any): any

  /**
   * Adds address fields to the order payload
   */
  addAddressFieldsToPayload(
    payload: any,
    storeDataToCustomer: boolean
  ): Promise<any>

  /**
   * Adds payment fields to the order payload
   */
  addPaymentFieldsToPayload(
    payload: any,
    storeDataToCustomer: boolean
  ): Promise<any>

  /**
   * Validates checkout data (strategy-specific validation)
   */
  validateCheckoutData(): boolean

  /**
   * Gets the email for the checkout
   */
  getEmail(): string | null | undefined

  /**
   * Gets the useSameBillingAddress flag
   */
  getUseSameBillingAddress(): boolean | null | undefined

  /**
   * Gets the subscribeToNewsletter flag
   */
  getSubscribeToNewsletter(): boolean | null | undefined

  /**
   * Gets the doiOptIn flag (VIP-12044)
   */
  getDoiOptIn(): boolean | null | undefined

  /**
   * Gets age data for age verification
   */
  getAgeData(): any

  /**
   * Gets phone number
   */
  getPhoneNumber(): string | null | undefined

  /**
   * Blacklists a payment method (by ID or code) after a failed order
   */
  blacklistPaymentMethod(idOrCode: number | string): void

  /**
   * Clears the selected payment method
   */
  clearPaymentMethod(): void

  /**
   * Gets the payment method ID or code for blacklisting
   */
  getPaymentMethodIdentifier(): PaymentMethodIdentifier

  /**
   * Clears age and phone data from the strategy's store
   */
  clearAgeAndPhoneData(): void

  /**
   * Performs cleanup after successful purchase
   */
  afterPurchaseCleanup(newCheckoutStore: {
    checkoutError?: string | null
    clearData: () => void
  }): void
}

/**
 * Regular checkout strategy for authenticated users.
 *
 * - Uses address/payment IDs when available, saves new data to customer account
 * - Calls ApiController.placeOrder()
 * - Supports "store as default" option for addresses and payment methods
 */
export class RegularCheckoutStrategy
  extends BaseCheckoutStrategy
  implements CheckoutStrategy
{
  protected readonly store: ReturnType<typeof useRegularCheckoutStore>

  readonly isGuest = false
  readonly isOpc = false

  constructor() {
    super()
    this.store = useRegularCheckoutStore()
  }

  shouldUseExpressOrder(): boolean {
    return false
  }

  /**
   * Regular checkout never uses express payment methods.
   */
  isExpressPaymentMethod(): boolean {
    return false
  }

  getCheckoutType(): CheckoutType {
    return 'regular'
  }

  /**
   * Sets existing addresses and payment method as default if storeAddressPaymentAsDefault is enabled.
   */
  async beforeOrder(): Promise<void> {
    if (this.store.storeAddressPaymentAsDefault) {
      await this.store._setExistingAddressesAsDefault()
    }
  }

  /**
   * Adds store_address_payment_as_default flag to payload if enabled.
   */
  modifyPayload(payload: any): any {
    if (this.store.storeAddressPaymentAsDefault) {
      payload.store_address_payment_as_default = true
    }
    return payload
  }

  async addAddressFieldsToPayload(
    payload: any,
    storeDataToCustomer: boolean
  ): Promise<any> {
    const addressesStore = useAddressesStore()

    // Shipping address
    if (this.store.selectedShippingAddressId) {
      payload.shipping_address_id = this.store.selectedShippingAddressId
    } else if (this.store.selectedShippingAddressData) {
      if (storeDataToCustomer) {
        const shippingAddressData = {
          ...this.store.selectedShippingAddressData,
          is_default_shipping_address: this.store.storeAddressPaymentAsDefault
        }
        const shippingAddressId =
          await addressesStore.saveAddress(shippingAddressData)
        this.store.selectedShippingAddressId = shippingAddressId
        payload.shipping_address_id = shippingAddressId
      } else {
        payload.shipping_address = this.store.selectedShippingAddressData
      }
    }

    // Billing address
    if (this.store.selectedBillingAddressId) {
      payload.billing_address_id = this.store.selectedBillingAddressId
    } else if (this.store.selectedBillingAddressData) {
      if (storeDataToCustomer) {
        const billingAddressData = {
          ...this.store.selectedBillingAddressData,
          is_default_billing_address: this.store.storeAddressPaymentAsDefault
        }
        const billingAddressId =
          await addressesStore.saveAddress(billingAddressData)
        this.store.selectedBillingAddressId = billingAddressId
        this.store.selectedBillingAddressData = null
        payload.billing_address_id = billingAddressId
      } else {
        payload.billing_address = this.store.selectedBillingAddressData
      }
    } else if (this.store.useSameBillingAddress) {
      if (payload.shipping_address_id) {
        if (storeDataToCustomer) {
          const shippingAddressData = this.store.selectedShippingAddressData
            ? { ...this.store.selectedShippingAddressData }
            : addressesStore.getAddressById(payload.shipping_address_id)

          if (shippingAddressData) {
            const billingAddressData = {
              ...shippingAddressData,
              is_default_billing_address:
                this.store.storeAddressPaymentAsDefault &&
                this.store.useSameBillingAddress
            }
            const billingAddressId =
              await addressesStore.saveAddress(billingAddressData)
            this.store.selectedBillingAddressId = billingAddressId
            payload.billing_address_id = billingAddressId
            if (this.store.selectedShippingAddressData) {
              this.store.selectedShippingAddressData = null
            }
          } else {
            payload.billing_address_id = payload.shipping_address_id
          }
        } else {
          payload.billing_address_id = payload.shipping_address_id
        }
      } else if (payload.shipping_address) {
        payload.billing_address = payload.shipping_address
      }
    }

    return payload
  }

  async addPaymentFieldsToPayload(
    payload: any,
    storeDataToCustomer: boolean
  ): Promise<any> {
    const paymentStore = usePaymentStore()

    // Prepayment
    if (
      this.store.selectedPaymentMethodData?.method_code === 'prepayment' &&
      this.store.selectedPaymentMethodData
    ) {
      payload.payment_method = this.store.selectedPaymentMethodData
      payload.payment_method_id = 0
      return payload
    }

    if (this.store.selectedPaymentMethodData && storeDataToCustomer) {
      const paymentMethodDataToSave = {
        ...this.store.selectedPaymentMethodData
      }
      delete paymentMethodDataToSave.bin
      delete paymentMethodDataToSave.card_bin
      delete paymentMethodDataToSave.customer_payment_method_id

      const paymentMethodId = await paymentStore.savePaymentMethod({
        ...paymentMethodDataToSave,
        is_default_payment_method: false
      })
      this.store.selectedPaymentMethodData = null
      this.store.selectedPaymentMethodId = paymentMethodId
      const paymentMethod = paymentStore.getPaymentMethodById(paymentMethodId!)
      if (!paymentMethod) {
        throw new Error('Payment method not found after saving')
      }
      payload.payment_method_id = paymentMethod.customer_payment_method_id
    } else if (this.store.selectedPaymentMethodData) {
      const paymentMethod = { ...this.store.selectedPaymentMethodData }
      payload.payment_method = paymentMethod
      payload.braintree_nonce =
        (this.store.selectedPaymentMethodData as any).tokenized_nonce ??
        (this.store.selectedPaymentMethodData as any).nonce
    } else if (this.store.selectedPaymentMethodId) {
      const paymentMethod = paymentStore.getPaymentMethodById(
        this.store.selectedPaymentMethodId
      )
      if (!paymentMethod) {
        throw new Error(
          `Payment method with ID ${this.store.selectedPaymentMethodId} not found`
        )
      }
      payload.payment_method_id = paymentMethod.customer_payment_method_id
    } else {
      throw new Error('No payment method selected')
    }

    return payload
  }

  validateCheckoutData(): boolean {
    // Regular checkout validation is handled by form validation
    return true
  }

  blacklistPaymentMethod(idOrCode: number | string): void {
    if (typeof idOrCode === 'number') {
      this.store.blacklistedPaymentMethodIds.push(idOrCode)
    }
  }

  clearPaymentMethod(): void {
    this.store.selectedPaymentMethodId = null
    this.store.selectedPaymentMethodData = null
  }

  getPaymentMethodIdentifier(): PaymentMethodIdentifier {
    return this.store.selectedPaymentMethodId
  }

  afterPurchaseCleanup(newCheckoutStore: {
    checkoutError?: string | null
    clearData: () => void
  }): void {
    newCheckoutStore.checkoutError = null
    this.store.blacklistedPaymentMethodIds = []
    this.store.clearData()
    newCheckoutStore.clearData()
  }
}

/**
 * Guest checkout strategy for non-authenticated users.
 *
 * - Uses address/payment data directly (no IDs)
 * - Calls ApiController.placeExpressOrder()
 * - No data is saved to any customer account
 */
export class GuestCheckoutStrategy
  extends DataBasedPaymentMethodStrategy
  implements CheckoutStrategy
{
  protected readonly store: ReturnType<typeof useGuestCheckoutStore>

  readonly isGuest = true
  readonly isOpc = false

  constructor() {
    super()
    this.store = useGuestCheckoutStore()
  }

  shouldUseExpressOrder(): boolean {
    return true
  }

  getCheckoutType(): CheckoutType {
    return this.isExpressPaymentMethod() ? 'express' : 'guest'
  }

  // No beforeOrder needed - guest checkout doesn't need pre-processing

  // No modifyPayload needed - guest checkout doesn't need payload modifications

  async addAddressFieldsToPayload(
    payload: any,
    storeDataToCustomer: boolean
  ): Promise<any> {
    if (this.store.selectedShippingAddressData) {
      payload.shipping_address = ensureAddressFields(
        this.store.selectedShippingAddressData
      )
    }

    if (this.store.selectedBillingAddressData) {
      payload.billing_address = ensureAddressFields(
        this.store.selectedBillingAddressData
      )
    } else if (this.store.selectedShippingAddressData) {
      payload.billing_address = ensureAddressFields(
        this.store.selectedShippingAddressData
      )
    }

    return payload
  }

  async addPaymentFieldsToPayload(
    payload: any,
    storeDataToCustomer: boolean
  ): Promise<any> {
    if (!this.store.selectedPaymentMethodData) {
      throw new Error('Payment method data is required for guest checkout')
    }

    const paymentMethodData = this.store.selectedPaymentMethodData as Record<
      string,
      any
    >
    if (paymentMethodData?.method_code === 'prepayment') {
      payload.payment_method = this.store.selectedPaymentMethodData
      payload.payment_method_id = 0
      return payload
    }

    const paymentData = getPaymentData(this.store.selectedPaymentMethodData)
    return {
      ...payload,
      ...paymentData
    }
  }

  validateCheckoutData(): boolean {
    if (!this.store.email) {
      MessageHelper.addErrorMessage(translate('checkout.error.emailRequired'))
      return false
    }
    return super.validateCheckoutData()
  }

  afterPurchaseCleanup(_newCheckoutStore: {
    checkoutError?: string | null
    clearData: () => void
  }): void {
    // Guest checkout only needs to clear its own store data
    this.store.clearData()
  }
}

/**
 * OPC (One-Page Checkout) strategy for quick product purchases.
 *
 * - Saves address/payment to customer account if authenticated
 * - Uses express order for certain payment methods (PayPal Express, Apple Pay, Google Pay)
 * - Adds product to cart as part of the checkout flow
 */
export class OpcCheckoutStrategy
  extends DataBasedPaymentMethodStrategy
  implements CheckoutStrategy
{
  protected readonly store: ReturnType<typeof useOpcStore>
  private productId: number = 0
  private campaignId: number = 0
  private qty: number = 1

  readonly isGuest = false
  readonly isOpc = true

  constructor() {
    super()
    this.store = useOpcStore()
  }

  /**
   * Sets the product details for the OPC order.
   * Must be called before placing the order.
   */
  setProduct(productId: number, campaignId: number, qty: number = 1): void {
    this.productId = productId
    this.campaignId = campaignId
    this.qty = qty
  }

  shouldUseExpressOrder(): boolean {
    const customerStore = useCustomerStore()
    if (!customerStore.customerActive) {
      return true
    }
    return this.isExpressPaymentMethod()
  }

  getCheckoutType(): CheckoutType {
    return 'opc'
  }

  /**
   * Adds the product to the cart before placing the order.
   */
  async beforeOrder(): Promise<void> {
    const cartStore = useCartStore()
    await cartStore.addItem(
      {
        productId: this.productId,
        campaignId: this.campaignId,
        qty: this.qty
      },
      false
    )
  }

  /**
   * Handles original nonce if 3DS didn't return one.
   */
  modifyPayload(payload: any): any {
    if (!payload.braintree_nonce && payload.payment_method) {
      const paymentMethod = payload.payment_method as Record<string, unknown>
      const originalNonce =
        (paymentMethod?.tokenized_nonce as string | undefined) ??
        (paymentMethod?.nonce as string | undefined)
      if (originalNonce) {
        payload.braintree_nonce = originalNonce
      }
    }
    return payload
  }

  async addAddressFieldsToPayload(
    payload: any,
    storeDataToCustomer: boolean
  ): Promise<any> {
    const customerStore = useCustomerStore()
    const addressesStore = useAddressesStore()
    await customerStore.waitUntilPending()

    // Shipping address
    if (this.store.selectedShippingAddressData) {
      const shippingAddressData = ensureAddressFields(
        this.store.selectedShippingAddressData
      )
      if (customerStore.customerActive) {
        shippingAddressData.is_default_shipping_address = storeDataToCustomer
        const shippingAddressId =
          await addressesStore.saveAddress(shippingAddressData)
        payload.shipping_address_id = shippingAddressId
        payload.shipping_address = shippingAddressData
      } else {
        payload.shipping_address = shippingAddressData
      }
    }

    // Billing address
    if (this.store.selectedBillingAddressData) {
      const billingAddressData = ensureAddressFields(
        this.store.selectedBillingAddressData
      )
      if (customerStore.customerActive) {
        billingAddressData.is_default_billing_address = storeDataToCustomer
        const billingAddressId =
          await addressesStore.saveAddress(billingAddressData)
        payload.billing_address_id = billingAddressId
        payload.billing_address = billingAddressData
      } else {
        payload.billing_address = billingAddressData
      }
    } else if (this.store.selectedShippingAddressData) {
      const billingAddressData = ensureAddressFields(
        this.store.selectedShippingAddressData
      )
      if (customerStore.customerActive) {
        billingAddressData.is_default_billing_address = storeDataToCustomer
        const billingAddressId =
          await addressesStore.saveAddress(billingAddressData)
        payload.billing_address_id = billingAddressId
        payload.billing_address = billingAddressData
      } else {
        payload.billing_address = billingAddressData
      }
    }

    return payload
  }

  async addPaymentFieldsToPayload(
    payload: any,
    storeDataToCustomer: boolean
  ): Promise<any> {
    if (!this.store.selectedPaymentMethodData) {
      throw new Error('Payment method data is required for OPC checkout')
    }

    const customerStore = useCustomerStore()
    const paymentStore = usePaymentStore()
    await customerStore.waitUntilPending()

    const paymentMethodData = this.store.selectedPaymentMethodData

    // Prepayment
    if (paymentMethodData?.method_code === 'prepayment') {
      payload.payment_method = this.store.selectedPaymentMethodData
      payload.payment_method_id = 0
      return payload
    }

    // Express payment methods (PayPal Express, Apple Pay, Google Pay) must never
    // be vaulted. Saving them hits POST /v2/customer/payment-methods, which the
    // backend rejects (e.g. "Pre-Approved Payment enabled PayPal account required
    // for vaulting") — see VIP-11941. These always use the express order endpoint,
    // which expects the payment object directly, so pass the payment data instead.
    if (this.isExpressPaymentMethod()) {
      const paymentData = getPaymentData(this.store.selectedPaymentMethodData)
      return {
        ...payload,
        ...paymentData,
        payment_method_id: null
      }
    }

    // Always save payment method if authenticated to get ID
    if (customerStore.customerActive) {
      const paymentMethodDataToSave = {
        ...this.store.selectedPaymentMethodData
      }
      delete paymentMethodDataToSave.bin
      delete paymentMethodDataToSave.card_bin
      delete paymentMethodDataToSave.customer_payment_method_id

      const paymentMethodId = await paymentStore.savePaymentMethod({
        ...paymentMethodDataToSave,
        is_default_payment_method: storeDataToCustomer
      })

      if (paymentMethodId) {
        const paymentMethod = paymentStore.getPaymentMethodById(paymentMethodId)
        if (!paymentMethod) {
          throw new Error('Payment method not found after saving')
        }
        payload.payment_method_id = paymentMethod.customer_payment_method_id
        payload.payment_method = paymentMethod
      } else {
        const paymentData = getPaymentData(this.store.selectedPaymentMethodData)
        return {
          ...payload,
          ...paymentData
        }
      }
    } else {
      // For guest checkout, set payment_method_id to null when providing payment object
      const paymentData = getPaymentData(this.store.selectedPaymentMethodData)
      return {
        ...payload,
        ...paymentData,
        payment_method_id: null
      }
    }

    return payload
  }

  afterPurchaseCleanup(newCheckoutStore: {
    checkoutError?: string | null
    clearData: () => void
  }): void {
    newCheckoutStore.checkoutError = null
    this.store.blacklistedPaymentMethodCodes = []
    this.store.clearCheckoutData()
    newCheckoutStore.clearData()
  }
}
